import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarSelectionStateByTab,
  CalendarTabValue,
  DateObject,
  SelectedDateType,
  TabDatesSelection,
} from './Calendar.types';
import { CALENDAR_START_DATE } from '../../constants';
import {
  getDatesBadgeLabel,
  getDaysBadgeLabel,
  getFormattedDate,
  getFormattedDates,
  getInitialCalendarStateByTab,
  resolveDatesForTab,
} from '../../utils';

const DEFAULT_CALENDAR_TAB = CalendarTabValue.MULTIPLE;

type UseCalendarTabStateOptions = {
  calendarTab?: CalendarTabValue;
  selectedStartDate?: DateObject | null;
  selectedEndDate?: DateObject | null;
  night: string;
  nights: string;
  days?: string;
  multipleDays: string;
  singleDay: string;
  isEventSpaceTabActive?: boolean;
  onChange?: (dates: string, startDate: string, endDate: string) => void;
  onCalendarTabChange?: (tab: CalendarTabValue) => void;
};

export const useCalendarTabState = ({
  calendarTab,
  selectedStartDate,
  selectedEndDate,
  night,
  nights,
  days,
  multipleDays,
  singleDay,
  isEventSpaceTabActive = false,
  onChange,
  onCalendarTabChange,
}: UseCalendarTabStateOptions) => {
  const [internalCalendarTab, setInternalCalendarTab] = useState<CalendarTabValue>(calendarTab || DEFAULT_CALENDAR_TAB);
  const [calendarStateByTab, setCalendarStateByTab] = useState<CalendarSelectionStateByTab>(() =>
    getInitialCalendarStateByTab(selectedStartDate, selectedEndDate, calendarTab || DEFAULT_CALENDAR_TAB)
  );

  const onChangeRef = useRef(onChange);

  const currentActiveTab = calendarTab || internalCalendarTab;
  const calendarState = calendarStateByTab[currentActiveTab];
  const { startDate, endDate } = calendarState;

  const datesLabel =
    currentActiveTab === CalendarTabValue.SINGLE
      ? ''
      : isEventSpaceTabActive
      ? getDaysBadgeLabel(startDate, endDate, days || '')
      : getDatesBadgeLabel(startDate, endDate, night, nights);

  const displayState = {
    datesLabel,
    showDatesBadgeContent: !!datesLabel,
    dateInputValue: getFormattedDates(startDate, endDate, 'dateWithMonth'),
    formattedStartDate: getFormattedDate(startDate),
    formattedEndDate: getFormattedDate(endDate),
  };

  const tabList = useMemo(
    () => [
      { tabTitle: multipleDays, tabValue: CalendarTabValue.MULTIPLE, isActive: true, tabSubtitle: '' },
      { tabTitle: singleDay, tabValue: CalendarTabValue.SINGLE, tabSubtitle: '' },
    ],
    [multipleDays, singleDay]
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (calendarTab) {
      setInternalCalendarTab(calendarTab);
    }
  }, [calendarTab]);

  useEffect(() => {
    onChangeRef.current?.(displayState.dateInputValue, displayState.formattedStartDate, displayState.formattedEndDate);
  }, [displayState.dateInputValue, displayState.formattedEndDate, displayState.formattedStartDate]);

  const updateCalendarState = useCallback(
    (
      tab: CalendarTabValue,
      selectedStartDate: DateObject | null,
      selectedEndDate: DateObject | null,
      selectedDateType: SelectedDateType
    ) => {
      setCalendarStateByTab(prevState => ({
        ...prevState,
        [tab]: {
          ...prevState[tab],
          startDate: selectedStartDate,
          endDate: selectedEndDate,
          selectedDateType,
        },
      }));
    },
    []
  );

  const applyTabDates = useCallback(
    (tab: CalendarTabValue, datesSelection: TabDatesSelection) => {
      updateCalendarState(tab, datesSelection.startDate, datesSelection.endDate, datesSelection.selectedDateType);
    },
    [updateCalendarState]
  );

  const setInitialDates = useCallback(
    (
      tab: CalendarTabValue,
      startDate: DateObject,
      endDate: DateObject,
      _defaultStartDate: DateObject,
      _defaultEndDate: DateObject,
      selectedDateType: SelectedDateType
    ) => {
      const resolvedDates = resolveDatesForTab(tab, startDate, endDate, selectedDateType);
      applyTabDates(tab, resolvedDates);
    },
    [applyTabDates]
  );

  const updateCurrentActiveTab = useCallback(
    (tabValue: CalendarTabValue) => {
      setCalendarStateByTab(prevState => ({
        ...prevState,
        [tabValue]: {
          startDate: null,
          endDate: null,
          selectedDateType: CALENDAR_START_DATE as SelectedDateType,
        },
      }));
      if (!calendarTab) {
        setInternalCalendarTab(tabValue);
      }
      onCalendarTabChange?.(tabValue);
    },
    [calendarTab, onCalendarTabChange]
  );

  const setDatesForCurrentTab = useCallback(
    (startDate: DateObject | null, endDate: DateObject | null, selectedDateType: SelectedDateType) => {
      const resolvedDates = resolveDatesForTab(currentActiveTab, startDate, endDate, selectedDateType);
      applyTabDates(currentActiveTab, resolvedDates);
    },
    [currentActiveTab, applyTabDates]
  );

  return {
    currentActiveTab,
    calendarState,
    startDate,
    endDate,
    displayState,
    tabList,
    setCalendarStateByTab,
    setInitialDates,
    updateCurrentActiveTab,
    setDatesForCurrentTab,
  };
};
